import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, Users, RefreshCw, ChevronRight, ExternalLink, Mail, Phone } from "lucide-react";

// =============================================================================
// VENDORS — Surveydrafts.us roll-up
// Reads from the local Mongo cache populated by the extended sync.
// Click a vendor row to expand its team roster and jump to filtered Jobs.
// =============================================================================
export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState(null);     // vendor.surveydrafts_id
  const [teamCache, setTeamCache] = useState({});     // { [vendorId]: members[] }
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/integrations/surveydrafts/vendors-cache")
      .then(({ data }) => setVendors(data || []))
      .catch(() => setVendors([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const onSyncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/jobs/sync-surveydrafts-extended");
      if (data.ok === false) {
        toast.error(data.reason || "Sync failed");
      } else {
        const c = data.counts || {};
        toast.success(
          `Synced: vendors +${c.vendors?.created || 0}/${c.vendors?.updated || 0} · ` +
          `team +${c.team_members?.created || 0}/${c.team_members?.updated || 0} · ` +
          `daily-jobs +${c.daily_jobs?.created || 0}/${c.daily_jobs?.updated || 0}`,
          { duration: 9000 },
        );
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed. Set X-API-Key on the Jobs board first.");
    }
    setSyncing(false);
  };

  const onToggle = async (vendor) => {
    const vid = vendor.surveydrafts_id;
    if (expanded === vid) { setExpanded(null); return; }
    setExpanded(vid);
    if (!teamCache[vid]) {
      try {
        const { data } = await api.get("/integrations/surveydrafts/team-cache", { params: { vendor_id: vid } });
        setTeamCache((m) => ({ ...m, [vid]: data || [] }));
      } catch {
        setTeamCache((m) => ({ ...m, [vid]: [] }));
      }
    }
  };

  const totals = useMemo(() => ({
    vendors: vendors.length,
    team: vendors.reduce((a, v) => a + (v.team_size || 0), 0),
    jobs: vendors.reduce((a, v) => a + (v.jobs_total || 0), 0),
    done: vendors.reduce((a, v) => a + (v.jobs_completed || 0), 0),
  }), [vendors]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return vendors;
    return vendors.filter((v) =>
      (v.name || "").toLowerCase().includes(s) ||
      (v.company || "").toLowerCase().includes(s) ||
      (v.owner_email || "").toLowerCase().includes(s),
    );
  }, [vendors, q]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="vendors-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">SURVEY DRAFTERS · ROLL-UP</div>
          <h1 className="font-heading text-4xl font-black tracking-tight leading-none mt-1">Teams</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            Live mirror from surveydrafts.us · click a row to view team roster
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search teams…"
            className="brut-input px-3 py-2 text-xs font-mono w-56"
            data-testid="vendors-search-input"
          />
          <button
            onClick={onSyncNow}
            disabled={syncing}
            className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-emerald-300 hover:bg-emerald-400 disabled:opacity-60"
            data-testid="vendors-sync-btn"
            title="Pull latest vendors, team and daily-jobs from surveydrafts.us"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "SYNCING…" : "SYNC NOW"}
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vendors-summary">
        <SummaryTile label="TEAMS" value={totals.vendors} testid="vendors-tile-count" />
        <SummaryTile label="MEMBERS" value={totals.team} testid="vendors-tile-team" />
        <SummaryTile label="JOBS TOTAL" value={totals.jobs.toLocaleString()} testid="vendors-tile-jobs" />
        <SummaryTile
          label="COMPLETED"
          value={`${totals.done.toLocaleString()} (${totals.jobs ? Math.round((totals.done / totals.jobs) * 100) : 0}%)`}
          testid="vendors-tile-done"
        />
      </div>

      {loading && <div className="font-mono text-xs text-zinc-500">Loading teams…</div>}

      {!loading && filtered.length === 0 && (
        <div className="brut-card p-10 text-center text-zinc-500 font-mono text-sm">
          NO TEAMS IN CACHE — CLICK <strong>SYNC NOW</strong> AFTER SETTING THE X-API-KEY ON THE JOBS BOARD.
        </div>
      )}

      {filtered.map((v) => (
        <VendorRow
          key={v.id}
          v={v}
          expanded={expanded === v.surveydrafts_id}
          onToggle={() => onToggle(v)}
          members={teamCache[v.surveydrafts_id] || null}
        />
      ))}
    </div>
  );
}

function SummaryTile({ label, value, testid }) {
  return (
    <div className="brut-card p-4" data-testid={testid}>
      <div className="label-mono text-zinc-500">{label}</div>
      <div className="font-heading text-3xl font-black tracking-tight mt-1">{value}</div>
    </div>
  );
}

function VendorRow({ v, expanded, onToggle, members }) {
  const pct = v.jobs_total ? Math.round(((v.jobs_completed || 0) / v.jobs_total) * 100) : 0;
  return (
    <div className="brut-card overflow-hidden" data-testid={`vendor-row-${v.surveydrafts_id || v.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 hover:bg-yellow-50 transition-colors text-left"
      >
        <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${expanded ? "rotate-90" : ""}`} />
        <div className="flex-1 min-w-0">
          <div className="font-heading text-lg font-black truncate" data-testid={`vendor-name-${v.surveydrafts_id || v.id}`}>
            {v.name || "Unnamed team"}
          </div>
          {v.company && v.company !== v.name && (
            <div className="text-xs text-zinc-600 font-mono truncate">{v.company}</div>
          )}
        </div>
        <div className="hidden md:flex items-center gap-2 text-xs font-mono">
          {v.account_status && (
            <span className={`px-2 py-1 border border-zinc-900 ${v.account_status === "active" ? "bg-emerald-100" : "bg-zinc-100"}`}>
              {String(v.account_status).toUpperCase()}
            </span>
          )}
        </div>
        <Stat label="TEAM" value={v.team_size || 0} />
        <Stat label="JOBS" value={(v.jobs_total || 0).toLocaleString()} />
        <Stat label="DONE" value={`${pct}%`} accent={pct >= 90 ? "good" : pct >= 50 ? "ok" : "warn"} />
      </button>

      {expanded && (
        <div className="border-t-2 border-zinc-900 bg-zinc-50 p-4 space-y-4" data-testid={`vendor-expanded-${v.surveydrafts_id || v.id}`}>
          <div className="flex flex-wrap gap-4 text-xs font-mono text-zinc-700">
            {v.owner_email && <Field icon={Mail} label="EMAIL" value={v.owner_email} />}
            {v.phone && <Field icon={Phone} label="PHONE" value={v.phone} />}
            {v.surveydrafts_id && <Field icon={ExternalLink} label="SD ID" value={v.surveydrafts_id.slice(0, 8) + "…"} />}
          </div>

          <div>
            <div className="label-mono mb-2 flex items-center gap-2">
              <Users className="w-3.5 h-3.5" /> TEAM ROSTER · GROUPED BY ROLE
            </div>
            {members === null && <div className="text-xs font-mono text-zinc-500">Loading roster…</div>}
            {members !== null && members.length === 0 && (
              <div className="text-xs font-mono text-zinc-500">No team members linked on surveydrafts.us yet.</div>
            )}
            {members !== null && members.length > 0 && (
              <RoleGroupedMembers members={members} />
            )}
          </div>

          <div className="pt-2">
            <Link
              to={v.surveydrafts_id
                ? `/jobs?vendor_id=${encodeURIComponent(v.surveydrafts_id)}&vendor_name=${encodeURIComponent(v.name || "")}`
                : "/jobs"}
              className="brut-btn inline-flex items-center gap-1 px-3 py-2 text-xs bg-emerald-300 hover:bg-emerald-400"
              data-testid={`vendor-view-jobs-${v.surveydrafts_id || v.id}`}
            >
              <Briefcase className="w-3.5 h-3.5" /> VIEW JOBS FOR THIS TEAM
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const tone = accent === "good" ? "text-emerald-700" : accent === "warn" ? "text-amber-700" : "text-zinc-900";
  return (
    <div className="text-right min-w-[60px]">
      <div className="label-mono text-zinc-500">{label}</div>
      <div className={`font-heading text-base font-black ${tone}`}>{value}</div>
    </div>
  );
}

function RoleBadge({ role }) {
  const r = String(role).toLowerCase();
  const tone = r.includes("admin") ? "bg-rose-100"
    : r.includes("qc") || r.includes("finalqc") ? "bg-indigo-100"
    : r.includes("draft") ? "bg-yellow-100"
    : r.includes("vendor") ? "bg-emerald-100"
    : r.includes("field") ? "bg-sky-100"
    : "bg-zinc-100";
  return (
    <span className={`px-1.5 py-0.5 border border-zinc-900 text-[9px] font-mono uppercase ${tone}`}>{r}</span>
  );
}

// Role order (matches surveydrafts.us pipeline)
const ROLE_GROUP_ORDER = [
  { match: ["field", "fieldcrew", "crew"], label: "Field Crew",   tone: "bg-sky-100" },
  { match: ["draft", "drafter"],           label: "Drafter",      tone: "bg-yellow-100" },
  { match: ["qaqc", "qa", "qc"],           label: "QA / QC",      tone: "bg-indigo-100" },
  { match: ["finalqc", "final"],           label: "Final QC",     tone: "bg-purple-100" },
  { match: ["manager", "lead"],            label: "Team Manager", tone: "bg-emerald-100" },
  { match: ["admin"],                      label: "Admin",        tone: "bg-rose-100" },
  { match: ["vendor", "owner"],            label: "Vendor Owner", tone: "bg-amber-100" },
];

function _roleBucketKey(role) {
  if (!role) return null;
  const r = String(role).toLowerCase().replace(/\d+$/, "");
  for (let i = 0; i < ROLE_GROUP_ORDER.length; i++) {
    if (ROLE_GROUP_ORDER[i].match.some((m) => r.includes(m))) return i;
  }
  return ROLE_GROUP_ORDER.length;
}

function RoleGroupedMembers({ members }) {
  const groups = React.useMemo(() => {
    const map = new Map();
    for (const m of members) {
      const k = _roleBucketKey(m.role);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(m);
    }
    return [...map.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([idx, ms]) => ({
        def: ROLE_GROUP_ORDER[idx] || { label: "Other", tone: "bg-zinc-100" },
        members: ms,
      }));
  }, [members]);

  return (
    <div className="space-y-3">
      {groups.map((g, i) => (
        <div key={i}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`px-1.5 py-0.5 border border-zinc-900 text-[9px] font-mono uppercase ${g.def.tone}`}>{g.def.label}</span>
            <span className="text-[10px] font-mono text-zinc-500">· {g.members.length}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {g.members.map((m) => (
              <div key={m.id} className="border border-zinc-900 bg-white p-3" data-testid={`team-member-${m.surveydrafts_id || m.id}`}>
                <div className="font-bold text-sm truncate" title={m.name}>{m.name || "—"}</div>
                {m.email && <div className="text-[10px] font-mono text-zinc-600 truncate mt-1" title={m.email}>{m.email}</div>}
                {m.phone && <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{m.phone}</div>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="w-3 h-3 text-zinc-500" />
      <span className="label-mono">{label}:</span>
      <span className="text-zinc-900">{value}</span>
    </div>
  );
}
